/**
 * Dettaglio di un test di misura: come si calcola, gli attributi valutati per
 * famiglia, le istruzioni e le esecuzioni nella squadra aperta, ognuno nella
 * sua card come il dettaglio delle schede. Con più misure ogni misura ha la
 * sua card, con la regola, i parametri e gli attributi. Sotto il titolo
 * metodo, unità e, se ci sono, RPE e minuti di riferimento. Mister e
 * Dirigente eseguono i test attivi, modificano, disattivano ed eliminano
 * anche i test degli altri; l'Admin legge.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ETICHETTA_FAMIGLIA_ATTRIBUTO,
  ETICHETTA_METODO_TEST,
  FAMIGLIE_ATTRIBUTO,
  puo,
} from '@calcio/shared';
import {
  CalculatorIcon,
  CalendarIcon,
  GaugeIcon,
  HistoryIcon,
  ListChecksIcon,
  PencilIcon,
  PlayIcon,
  PowerIcon,
  PowerOffIcon,
  ScrollTextIcon,
  Trash2Icon,
  UsersIcon,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { chiama } from '../api/client.js';
import { AzioniTestata, MenuAzioni, PulsantePrincipale } from '../comuni/Azioni.js';
import { Corpo } from '../comuni/Corpo.js';
import { dataBreve, messaggio, useDati } from '../comuni/dati.js';
import { IconaFamiglia } from '../comuni/famiglie.js';
import { usePaginazione } from '../comuni/Paginazione.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { useContesto } from '../navigazione/contesto.js';
import { CardSezione } from './CardSezione.js';
import { DialogTest } from './DialogTest.js';
import type { EsecuzioneInElenco } from './esecuzione.js';
import { IconaMetodi } from './icone.js';
import {
  CAMPI_PARAMETRI,
  formulaAParole,
  metodiDelTest,
  unitaDelCampo,
  type AttributoDelTest,
  type MisuraDelTest,
  type TestDettaglio,
} from './test-misura.js';
import { Nota } from '../comuni/Nota.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

export function DettaglioTest() {
  const { ss, id } = useParams<{ ss: string; id: string }>();
  const navigate = useNavigate();
  const { utente } = useContesto();
  const { dati, errore, ricarica } = useDati<TestDettaglio>(id ? `/test/${id}` : '');
  const esecuzioni = useDati<EsecuzioneInElenco[]>(
    ss && id ? `/squadre-stagione/${ss}/test/${id}/esecuzioni` : '',
  );
  const [eliminaAperta, setEliminaAperta] = useState(false);
  const [modificaAperta, setModificaAperta] = useState(false);
  const scrive = Boolean(ss && puo(utente.assegnazioni, 'misurazione.scrivi', ss));
  const elenco = `/sq/${ss}/test`;

  if (!dati) {
    return (
      <>
        <Testata titolo="Test" indietro={elenco} />
        <Corpo>
          <StatoCaricamento errore={errore} />
        </Corpo>
      </>
    );
  }

  const cambiaStato = async () => {
    try {
      await chiama(`/test/${dati.id}/stato`, { metodo: 'PUT', corpo: { attivo: !dati.attivo } });
      await ricarica();
      toast.success(dati.attivo ? 'Test disattivato.' : 'Test riattivato.');
    } catch (e) {
      toast.error(messaggio(e));
    }
  };

  const elimina = async () => {
    try {
      await chiama(`/test/${dati.id}`, { metodo: 'DELETE' });
      toast.success(`${dati.nome} eliminato.`);
      navigate(elenco, { replace: true });
    } catch (e) {
      toast.error(messaggio(e));
      setEliminaAperta(false);
    }
  };

  const metodi = metodiDelTest(dati);
  const [sola] = dati.misure.length === 1 ? dati.misure : [];

  return (
    <>
      <Testata
        titolo={dati.nome}
        indietro={elenco}
        azione={
          scrive && (
            <AzioniTestata>
              {dati.attivo ? (
                <PulsantePrincipale
                  etichetta="Esegui"
                  Icona={PlayIcon}
                  a={`${elenco}/${dati.id}/esegui`}
                />
              ) : (
                <PulsantePrincipale
                  etichetta="Modifica"
                  Icona={PencilIcon}
                  onClick={() => setModificaAperta(true)}
                />
              )}
              <MenuAzioni
                voci={[
                  dati.attivo && {
                    etichetta: 'Modifica',
                    Icona: PencilIcon,
                    onSelect: () => setModificaAperta(true),
                  },
                  {
                    etichetta: dati.attivo ? 'Disattiva' : 'Riattiva',
                    Icona: dati.attivo ? PowerOffIcon : PowerIcon,
                    onSelect: () => void cambiaStato(),
                  },
                  {
                    etichetta: 'Elimina',
                    Icona: Trash2Icon,
                    onSelect: () => setEliminaAperta(true),
                    distruttiva: true,
                  },
                ]}
              />
            </AzioniTestata>
          )
        }
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="flex items-center gap-1 font-medium">
            <IconaMetodi metodi={metodi} className="size-4 text-muted-foreground" />
            {metodi.map((m) => ETICHETTA_METODO_TEST[m]).join(', ')}
          </span>
          {sola ? (
            sola.unita && <span className="text-muted-foreground">Risultato in {sola.unita}</span>
          ) : (
            <span className="text-muted-foreground">{dati.misure.length} misure</span>
          )}
          {dati.rpeRiferimento !== null && (
            <span
              className="flex items-center gap-1 text-muted-foreground"
              title="Carico di riferimento in seduta"
            >
              <GaugeIcon aria-hidden className="size-4" />
              <span className="tabular-nums">
                RPE {dati.rpeRiferimento} · {dati.minutiRiferimento} min
              </span>
            </span>
          )}
          {!dati.attivo && <Badge variant="outline">Disattivato</Badge>}
        </div>
      </Testata>
      <Corpo className="flex flex-col gap-4">
        {/* Due colonne quando la vista è larga; le card di una riga sono alte uguali. */}
        <div className="grid gap-4 @5xl:grid-cols-2">
          {sola ? (
            <>
              <CardSezione titolo="Come si calcola" Icona={CalculatorIcon}>
                <Regola misura={sola} />
              </CardSezione>
              <CardSezione
                titolo="Attributi valutati"
                Icona={ListChecksIcon}
                conteggio={sola.attributi.length}
              >
                <Nota className="px-1 text-center">
                  Lo svolge chiunque. Ogni attributo prende lo stesso punteggio.
                </Nota>
                <AttributiPerFamiglia attributi={sola.attributi} />
              </CardSezione>
            </>
          ) : (
            dati.misure.map((m) => (
              <CardSezione key={m.id} titolo={m.nome} Icona={CalculatorIcon}>
                <Regola misura={m} />
                <Nota className="px-1 text-center">
                  Ognuno di questi attributi prende il punteggio della misura.
                </Nota>
                <AttributiPerFamiglia attributi={m.attributi} />
              </CardSezione>
            ))
          )}

          {dati.istruzioni && (
            <CardSezione titolo="Istruzioni" Icona={ScrollTextIcon}>
              <p className="rounded-lg border border-border bg-card p-3 text-sm whitespace-pre-line">
                {dati.istruzioni}
              </p>
            </CardSezione>
          )}
          <Esecuzioni
            esecuzioni={esecuzioni.dati}
            errore={esecuzioni.errore}
            attivo={dati.attivo}
            percorso={`${elenco}/${dati.id}/esecuzioni`}
          />
        </div>
      </Corpo>

      {scrive && (
        <DialogTest
          ss={ss!}
          aperto={modificaAperta}
          onChiudi={() => setModificaAperta(false)}
          esistente={dati}
          onSalvato={ricarica}
        />
      )}

      <AlertDialog open={eliminaAperta} onOpenChange={setEliminaAperta}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {dati.nome}?</AlertDialogTitle>
            <AlertDialogDescription>
              Il test esce dalla libreria e la cancellazione resta nel Registro attività. Per
              tenerlo senza usarlo, disattivalo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => void elimina()}>
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** La regola di una misura in una frase e i suoi parametri con l'unità. */
function Regola({ misura: m }: { misura: MisuraDelTest }) {
  const p = m.parametri as unknown as Record<string, number | number[]>;
  return (
    <>
      <p className="px-1 text-sm">{formulaAParole(m.parametri, m.unita)}</p>
      <dl className="flex flex-col gap-1.5 rounded-lg border border-border bg-card p-3 text-sm">
        {CAMPI_PARAMETRI[m.metodo].map((c) => {
          const u = unitaDelCampo(c, m.unita);
          const v = p[c.chiave] as number;
          return (
            <div key={c.chiave} className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">{c.etichetta}</dt>
              <dd className="font-medium tabular-nums">
                {v.toLocaleString('it-IT')}
                {u && ` ${u}`}
              </dd>
            </div>
          );
        })}
        {m.parametri.metodo === 'gradini' && (
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-muted-foreground">Navette per livello</dt>
            <dd className="text-right font-medium tabular-nums">
              {m.parametri.navettePerLivello.length === 1
                ? `${m.parametri.navettePerLivello[0]} per ogni livello`
                : m.parametri.navettePerLivello.join(', ')}
            </dd>
          </div>
        )}
      </dl>
    </>
  );
}

/** Gli attributi raggruppati per famiglia, quelli di una famiglia su una riga. */
function AttributiPerFamiglia({ attributi }: { attributi: AttributoDelTest[] }) {
  return FAMIGLIE_ATTRIBUTO.map((famiglia) => {
    const voci = attributi.filter((a) => a.famiglia === famiglia);
    if (voci.length === 0) return null;
    return (
      <section
        key={famiglia}
        className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3"
      >
        <h3 className="flex items-center gap-2 font-semibold">
          <IconaFamiglia famiglia={famiglia} className="size-5" />
          {ETICHETTA_FAMIGLIA_ATTRIBUTO[famiglia]}
        </h3>
        <ul className="flex flex-wrap items-center gap-x-1 gap-y-1.5 text-sm">
          {voci.map((a, i) => (
            <li key={a.id} className="flex items-center gap-2">
              <span className={a.attivo ? undefined : 'text-muted-foreground'}>{a.nome}</span>
              {!a.attivo && <Badge variant="outline">Disattivato</Badge>}
              {i < voci.length - 1 && <span className="-ml-2">,</span>}
            </li>
          ))}
        </ul>
      </section>
    );
  });
}

/** Le esecuzioni nella squadra aperta, dalla più recente, dieci per pagina. */
function Esecuzioni({
  esecuzioni,
  errore,
  attivo,
  percorso,
}: {
  esecuzioni: EsecuzioneInElenco[] | null;
  errore: string | null;
  attivo: boolean;
  percorso: string;
}) {
  const { righe, barra, inizio } = usePaginazione(esecuzioni ?? [], 'pagina-esecuzioni');
  return (
    <div ref={inizio} className="scroll-mt-(--altezza-testata)">
      <CardSezione titolo="Esecuzioni" Icona={HistoryIcon} conteggio={esecuzioni?.length}>
        {!esecuzioni ? (
          <StatoCaricamento errore={errore} />
        ) : esecuzioni.length === 0 ? (
          <StatoVuoto
            titolo={attivo ? 'Ancora nessuna esecuzione in questa squadra' : 'Nessuna esecuzione'}
            descrizione={attivo ? undefined : 'Il test è disattivato: riattivalo per eseguirlo.'}
          />
        ) : (
          <>
            <ul className="flex flex-col gap-2">
              {righe.map((e) => (
                <li key={e.id}>
                  <Link
                    to={`${percorso}/${e.id}`}
                    className="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5"
                  >
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-background">
                      <CalendarIcon aria-hidden className="size-5" />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold tabular-nums">{dataBreve(e.data)}</span>
                        {e.blocco && <Badge variant="secondary">In seduta</Badge>}
                      </span>
                      {e.autore && (
                        <span className="truncate text-sm text-muted-foreground">
                          Registrata da {e.autore}
                        </span>
                      )}
                    </span>
                    <span
                      className="flex items-center gap-1 text-sm text-muted-foreground"
                      title="Giocatori misurati"
                    >
                      <UsersIcon aria-hidden className="size-4" />
                      <span className="font-medium text-foreground tabular-nums">
                        {e.giocatori}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            {barra}
          </>
        )}
      </CardSezione>
    </div>
  );
}
